import { useEffect } from 'react';
import { useAuth } from '@/context/auth/AuthContext';
import { fetchDevices } from '@/services/auth/device.service';
import { getDeviceId } from '@/utils/device';
import logger from '@/utils/logger';
import { postLoginCoordinator } from '@/services/auth/postLoginCoordinator';
import { networkService } from '@/services/network.service';

/**
 * Monitorizza lo stato del dispositivo corrente.
 * Se il dispositivo viene rimosso dall'account, l'utente viene disconnesso
 * non appena torna online.
 */
export const useDeviceMonitor = () => {
  const { user, signOut } = useAuth();

  useEffect(() => {
    if (!user) return;

    const deviceId = getDeviceId();
    let failureCount = 0;

    const checkDevice = async () => {
      if (postLoginCoordinator.isPostLogin()) return;
      if (localStorage.deviceRegistrationPending) return;
      try {
        const devices = await fetchDevices();
        const stillRegistered = devices.some(d => d.deviceId === deviceId);
        if (stillRegistered) {
          failureCount = 0;
        } else if (networkService.isOnline()) {
          failureCount++;
          if (failureCount >= 3) {
            const registeredAt = Number(localStorage.deviceRegisteredAt);
            const recentlyRegistered =
              registeredAt && Date.now() - registeredAt < 5 * 60 * 1000;
            if (recentlyRegistered) {
              // device appena registrato, concedi tempo extra
              failureCount = 0;
            } else {
              logger.warn(
                '[useDeviceMonitor] current device removed, signing out'
              );
              await signOut();
            }
          }
        }
      } catch (error) {
        logger.logError('useDeviceMonitor','check failed',{additionalInfo:error});
      }
    };

    // Primo controllo ritardato e successivamente ogni minuto
    const initialTimeout = setTimeout(checkDevice, 3000);
    const interval = setInterval(checkDevice, 60000);

    const resetAndCheck = () => {
      failureCount = 0;
      void checkDevice();
    };

    window.addEventListener('online', checkDevice);
    window.addEventListener('deviceRegistered', resetAndCheck);
    window.addEventListener('postLoginEnded', resetAndCheck);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      window.removeEventListener('online', checkDevice);
      window.removeEventListener('deviceRegistered', resetAndCheck);
      window.removeEventListener('postLoginEnded', resetAndCheck);
    };
  }, [user, signOut]);
};
